import { z } from 'zod';

/**
 * Wave 10 — "Problemas" / issue tracker.
 *
 * Punch-list entity. Foreman or field worker logs a problem from the
 * phone, engineer/PM assigns + tracks to resolution. An issue can be
 * scoped to a stage (`stageId`) or to the whole obra (null).
 *
 * Severity vocabulary tracks how brazilian construction teams triage:
 *   baixa     → informational / observação
 *   normal    → default punch-list item
 *   alta      → blocking a specific stage / needs today
 *   urgente   → safety / alvará risk / blocks handover
 *
 * Status progression: aberta → em_andamento → resolvida OR descartada.
 * `bloqueada` is a parking spot for "waiting on owner decision / third
 * party"; the app treats it as open for SLA math.
 *
 * Why pt-BR identifiers (and not low/medium/high/urgent)?
 * --------------------------------------------------------
 * Builder's enum convention is: DOMAIN terms in pt-BR, INFRA terms in
 * EN. Issue severity, issue status, stage status, and checklist
 * status are all *domain* — they're how the obra crew talks about the
 * work in everyday Portuguese — so the wire format mirrors that
 * vocabulary directly. Infra-flavoured enums (alert severity,
 * payment status, quotation status, etc.) stay in English because
 * they're pure system semantics that nobody on the obra speaks.
 * `issueSeverityToAlertSeverity` below bridges the two when an issue
 * needs to fan out into the alerts hub.
 */

export const issueSeveritySchema = z.enum([
  'baixa',
  'normal',
  'alta',
  'urgente',
]);
export type IssueSeverity = z.infer<typeof issueSeveritySchema>;

/**
 * Bridge from domain severity to infra alert severity.
 *
 * Used wherever an issue triggers a row in the alerts hub (where the
 * shared severity vocabulary is `critical | warning | info`). Keeping
 * this mapping in one place prevents cross-module drift — every place
 * that wants to render an issue as an alert reuses the same rule.
 */
export function issueSeverityToAlertSeverity(
  s: IssueSeverity,
): 'critical' | 'warning' | 'info' {
  switch (s) {
    case 'urgente':
      return 'critical';
    case 'alta':
      return 'warning';
    case 'normal':
    case 'baixa':
      return 'info';
  }
}

/**
 * Display label for an issue severity, in pt-BR.
 *
 * Capitalised form for UI surfaces — "Alta", "Normal", etc. — so we
 * avoid scattering ad-hoc capitalisations across components.
 */
export function issueSeverityLabel(s: IssueSeverity): string {
  switch (s) {
    case 'urgente': return 'Urgente';
    case 'alta':    return 'Alta';
    case 'normal':  return 'Normal';
    case 'baixa':   return 'Baixa';
  }
}

export const issueStatusSchema = z.enum([
  'aberta',
  'em_andamento',
  'bloqueada',
  'resolvida',
  'descartada',
]);
export type IssueStatus = z.infer<typeof issueStatusSchema>;

export const issueSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  projectId: z.string(),
  stageId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  severity: issueSeveritySchema,
  status: issueStatusSchema,
  dueDate: z.string().nullable(), // YYYY-MM-DD, SP local
  assigneeUserId: z.string().nullable(),
  reportedBy: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  attachmentIds: z.array(z.string()).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type Issue = z.infer<typeof issueSchema>;

export const createIssueRequestSchema = z.object({
  stageId: z.string().nullable().optional(),
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(5000).optional(),
  severity: issueSeveritySchema.optional(), // default: 'normal'
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'dueDate must be YYYY-MM-DD')
    .optional(),
  assigneeUserId: z.string().nullable().optional(),
  attachmentIds: z.array(z.string()).max(20).optional(),
});
export type CreateIssueRequest = z.infer<typeof createIssueRequestSchema>;

export const updateIssueRequestSchema = z.object({
  title: z.string().trim().min(3).max(200).optional(),
  description: z.string().trim().max(5000).nullable().optional(),
  severity: issueSeveritySchema.optional(),
  status: issueStatusSchema.optional(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'dueDate must be YYYY-MM-DD')
    .nullable()
    .optional(),
  stageId: z.string().nullable().optional(),
  assigneeUserId: z.string().nullable().optional(),
  attachmentIds: z.array(z.string()).max(20).optional(),
  resolutionNote: z.string().trim().max(2000).optional(),
});
export type UpdateIssueRequest = z.infer<typeof updateIssueRequestSchema>;

export const listIssuesQuerySchema = z.object({
  status: issueStatusSchema.optional(),
  severity: issueSeveritySchema.optional(),
  assigneeUserId: z.string().optional(),
  stageId: z.string().optional(),
  includeDeleted: z.boolean().optional(),
});
export type ListIssuesQuery = z.infer<typeof listIssuesQuerySchema>;
